import React, { useCallback, useContext, useEffect, useState } from 'react'
import { MarketingCampaignDetailDto } from 'api/Trading-Accounts/Types/MarketingCampaignDetailDto'
import { AddUpdateMarketingCampaign } from 'api/Transaction/Types/AddUpdateMarketingCampaign'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchTickmillCompaniesAction } from 'store/identity/identity.actions'
import { fetchMarginBonusWithdrawRulesAction } from 'store/marketing/marketing.actions'
import {
  fetchInstrumentsAction,
  fetchMarginBonusResultStatus,
  fetchPlatformTypesAction,
  fetchTradingAccountRestrictions,
  fetchTradingAccountTypeAction,
  getCampaignBusinessRuleTypesAction,
  getCampaignIntervalTypesAction,
  getCampaignStatusesAction,
  getCampaignTypesAction,
  getCurrenciesAction,
} from 'store/trading-accounts/trading-accounts.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { IdentityApiClient } from 'utils/api/identityApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useFetchOne } from 'utils/api/useFetch'
import { AuthSessionContext } from 'utils/AuthContext'
import { isEmpty } from 'utils/is-empty'
import { isNumeric } from 'utils/is-numeric'

import ClientAreaAuditLogModal from '../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { AddCampaignDetailsForm } from '../AddCampaignDetailsForm'
import { CampaignTab } from '../CampaignSlug'
import { CampaignNavigation } from '../Navigation/CampaignNavigation'
import { CampaignDetailsForm } from './CampaignDetailsForm'

interface OuterProps {
  data?: MarketingCampaignDetailDto

  afterSubmit(): void
}

const useMapStateToProps = () => ({
  tradingAccountTypes: useAppSelector((state) => state.tradingAccounts.tradingAccountType),
  platformTypes: useAppSelector((state) => state.tradingAccounts.platformTypes),
  intervalTypes: useAppSelector((state) => state.tradingAccounts.intervalTypes),
  campaignStatuses: useAppSelector((state) => state.tradingAccounts.campaignStatuses),
  currencies: useAppSelector((state) => state.tradingAccounts.currencies),
  campaignTypes: useAppSelector((state) => state.tradingAccounts.campaignTypes),
  campaignBusinessRuleTypes: useAppSelector(
    (state) => state.tradingAccounts.campaignBusinessRuleTypes
  ),
  instruments: useAppSelector((state) => state.tradingAccounts.instruments),
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
  tradingAccountRestrictions: useAppSelector((state) => state.tradingAccounts.restrictions),
  marginBonusWithdrawRules: useAppSelector((state) => state.marketing.marginBonusWithdrawRules),
  marginBonusResultStatus: useAppSelector((state) => state.tradingAccounts.marginBonusResultStatus),
})

const useMapDispatchToProps = () => {
  const identityApiClient = useApiClient(IdentityApiClient)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const dispatch = useAppDispatch()

  return {
    fetchTradingAccountTypes: async () =>
      dispatch(fetchTradingAccountTypeAction(tradingAccountsApiClient)),
    fetchCampaignIntervalTypes: async () =>
      dispatch(getCampaignIntervalTypesAction(tradingAccountsApiClient)),
    fetchCampaignStatuses: async () =>
      dispatch(getCampaignStatusesAction(tradingAccountsApiClient)),
    fetchCurrencies: async () => dispatch(getCurrenciesAction(tradingAccountsApiClient)),
    fetchCampaignTypes: async () => dispatch(getCampaignTypesAction(tradingAccountsApiClient)),
    fetchInstruments: async () => dispatch(fetchInstrumentsAction(tradingAccountsApiClient)),
    fetchTickmillCompanies: async () => dispatch(fetchTickmillCompaniesAction(identityApiClient)),
    fetchTradingAccountRestrictionsAction: async () =>
      dispatch(fetchTradingAccountRestrictions(tradingAccountsApiClient)),
    fetchCampaignBusinessRuleTypes: async () =>
      dispatch(getCampaignBusinessRuleTypesAction(tradingAccountsApiClient)),
    fetchPlatformTypes: async () => dispatch(fetchPlatformTypesAction(tradingAccountsApiClient)),
    fetchMarginBonusWithdrawRules: async () =>
      dispatch(fetchMarginBonusWithdrawRulesAction(tradingAccountsApiClient)),
    fetchMarginBonusResultStatus: async () =>
      dispatch(fetchMarginBonusResultStatus(tradingAccountsApiClient)),
  }
}

const useCampaignDetailsFetch = () => {
  const dispatch = useAppDispatch()

  const {
    tradingAccountTypes,
    campaignTypes,
    campaignStatuses,
    currencies,
    intervalTypes,
    instruments,
    platformTypes,
    tickmillCompanies,
    tradingAccountRestrictions,
    campaignBusinessRuleTypes,
    marginBonusWithdrawRules,
    marginBonusResultStatus,
  } = useMapStateToProps()

  const {
    fetchTradingAccountTypes,
    fetchCampaignStatuses,
    fetchCampaignTypes,
    fetchCampaignIntervalTypes,
    fetchTradingAccountRestrictionsAction,
    fetchCurrencies,
    fetchInstruments,
    fetchPlatformTypes,
    fetchTickmillCompanies,
    fetchCampaignBusinessRuleTypes,
    fetchMarginBonusWithdrawRules,
    fetchMarginBonusResultStatus,
  } = useMapDispatchToProps()

  useEffect(() => {
    if (isEmpty(tradingAccountTypes)) fetchTradingAccountTypes()
    if (isEmpty(platformTypes)) fetchPlatformTypes()
    if (isEmpty(intervalTypes)) fetchCampaignIntervalTypes()
    if (isEmpty(campaignStatuses)) fetchCampaignStatuses()
    if (isEmpty(currencies)) fetchCurrencies()
    if (isEmpty(campaignTypes)) fetchCampaignTypes()
    if (isEmpty(instruments)) fetchInstruments()
    if (isEmpty(tickmillCompanies)) fetchTickmillCompanies()
    if (isEmpty(campaignBusinessRuleTypes)) fetchCampaignBusinessRuleTypes()
    if (isEmpty(marginBonusWithdrawRules)) fetchMarginBonusWithdrawRules()
    // it's the same restrictions as for TA
    if (isEmpty(tradingAccountRestrictions)) fetchTradingAccountRestrictionsAction()
    if (isEmpty(marginBonusResultStatus)) fetchMarginBonusResultStatus()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatch,
    tradingAccountTypes,
    intervalTypes,
    campaignStatuses,
    currencies,
    campaignTypes,
    instruments,
    tickmillCompanies,
    campaignBusinessRuleTypes,
    marginBonusWithdrawRules,
    marginBonusResultStatus,
  ])

  return {
    tradingAccountTypes,
    platformTypes,
    campaignTypes,
    campaignStatuses,
    currencies,
    campaignBusinessRuleTypes,
    intervalTypes,
    instruments,
    tickmillCompanies,
    tradingAccountRestrictions,
    marginBonusWithdrawRules,
  }
}

const useCampaignTypeIdChange = (props: OuterProps) => {
  const { data } = props

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const [campaignTypeId, setCampaignTypeId] = useState<number | undefined>(
    isNumeric(data?.type?.id) ? data?.type?.id : undefined
  )

  const callback = useCallback(async () => {
    if (isNumeric(campaignTypeId)) {
      return await tradingAccountsApiClient.getCampaignCalculationTypesByCampaignTypeId(
        campaignTypeId
      )
    }
    return Promise.resolve([])
  }, [campaignTypeId]) // eslint-disable-line react-hooks/exhaustive-deps
  const { data: calculationTypes = [] } = useFetchOne(callback)

  const handleTypeIdChange = async (typeId: number) => {
    if (isNumeric(typeId)) {
      setCampaignTypeId(typeId)
    }
  }

  return { campaignTypeId, calculationTypes, handleTypeIdChange }
}

const CampaignDetailsPage: React.FC<OuterProps> = (props) => {
  const { data, afterSubmit } = props

  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)

  const setToast = useContext(ToastContext)
  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []
  const dateFormatType = auth?.user.dateFormatType.name

  const {
    tradingAccountTypes,
    platformTypes,
    campaignTypes,
    campaignStatuses,
    campaignBusinessRuleTypes,
    currencies,
    intervalTypes,
    instruments,
    tickmillCompanies,
    tradingAccountRestrictions,
    marginBonusWithdrawRules,
  } = useCampaignDetailsFetch()

  const { calculationTypes, campaignTypeId, handleTypeIdChange } = useCampaignTypeIdChange(props)
  const [auditLogVisible, setAuditLogVisible] = useState(false)

  const releaseCampaignRequest = async () => {
    if (data?.id) {
      await tradingAccountsApiClient.releaseMarketingCampaign(data.id)
      setToast(successToast('Trading accounts are now removed from the campaign.'))
    }
  }

  const handleSubmit = async (values: AddUpdateMarketingCampaign) => {
    if (data) {
      await tradingAccountsApiClient.updateMarketingCampaign(data.id, values)
    } else {
      await tradingAccountsApiClient.addMarketingCampaign(values)
    }
    setToast(successToast(`Marketing Campaign ${values.id ? 'edited' : 'added'} successfully.`))
    afterSubmit()
  }

  return (
    <>
      {auditLogVisible && (
        <ClientAreaAuditLogModal
          isVisible={auditLogVisible}
          onClose={() => setAuditLogVisible(false)}
          marketingCampaignId={data?.id}
        />
      )}

      {data && <CampaignNavigation data={data} tab={CampaignTab.DETAILS} />}
      {data && isNumeric(campaignTypeId) ? (
        <CampaignDetailsForm
          data={data}
          campaignTypeId={campaignTypeId}
          tradingAccountTypes={tradingAccountTypes}
          intervalTypes={intervalTypes}
          currencies={currencies}
          campaignTypes={campaignTypes}
          campaignStatuses={campaignStatuses}
          campaignBusinessRuleTypes={campaignBusinessRuleTypes}
          calculationTypes={calculationTypes}
          instruments={instruments}
          permissions={permissions}
          dateFormatType={dateFormatType}
          platformTypes={platformTypes}
          tickmillCompanies={tickmillCompanies}
          restrictions={tradingAccountRestrictions}
          setAuditLogModal={setAuditLogVisible}
          releaseCampaignRequest={releaseCampaignRequest}
          marginBonusWithdrawRules={marginBonusWithdrawRules}
          onTypeIdChange={handleTypeIdChange}
          onSubmit={handleSubmit}
        />
      ) : (
        <AddCampaignDetailsForm
          campaignTypeId={campaignTypeId}
          platformTypes={platformTypes}
          intervalTypes={intervalTypes}
          campaignStatuses={campaignStatuses}
          currencies={currencies}
          campaignTypes={campaignTypes}
          instruments={instruments}
          tickmillCompanies={tickmillCompanies}
          dateFormatType={dateFormatType}
          tradingAccountTypes={tradingAccountTypes}
          campaignBusinessRuleTypes={campaignBusinessRuleTypes}
          calculationTypes={calculationTypes}
          permissions={permissions}
          restrictions={tradingAccountRestrictions}
          marginBonusWithdrawRules={marginBonusWithdrawRules}
          onTypeIdChange={handleTypeIdChange}
          onSubmit={handleSubmit}
        />
      )}
    </>
  )
}

export default CampaignDetailsPage
